import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useEffect, useRef, useState } from 'react';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
import ScrollMarker from './components/_scroll-marker';
/**
 * _A component for showing scroll indicators for scrollable content._
 *
 * ***
 *
 * When placed inside a scrollable container the component displays shadow-like indicators on the sides the content can be scrolled.
 *
 * Scroll indicators can be displayed horizontally or vertically.
 */

export const ScrollIndicator = _ref => {
  let {
    children,
    direction = 'horizontal',
    fwdRef
  } = _ref;
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [showBottom, setShowBottom] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const element = ref.current;
    if (element) {
      const onScroll = () => {
        // Fix a rare browser rounding bug that can cause indicators to stay visible.
        // https://oppjira.eficode.com/browse/OPDS-5184
        const fixRounding = 1;
        const {
          offsetHeight = 0,
          offsetWidth = 0,
          scrollHeight = 0,
          scrollLeft = 0,
          scrollTop = 0,
          scrollWidth = 0
        } = element;
        if (direction === 'horizontal') {
          setShowLeft(scrollLeft > 0);
          setShowRight(scrollLeft + offsetWidth < scrollWidth - fixRounding);
          setShowTop(false);
          setShowBottom(false);
        }
        if (direction === 'vertical') {
          setShowLeft(false);
          setShowRight(false);
          setShowTop(scrollTop > 0);
          setShowBottom(scrollTop + offsetHeight < scrollHeight - fixRounding);
        }
      };
      onScroll();
      element.addEventListener('scroll', onScroll);
      return () => {
        element.removeEventListener('scroll', onScroll);
      };
    }
  }, [direction]);
  const componentClassName = buildVersionedClassNames('ds-scroll-indicator');
  const containerClassName = buildVersionedClassNames('ds-scroll-indicator__container', {
    'ds-scroll-indicator__container--horizontal': direction === 'horizontal',
    'ds-scroll-indicator__container--vertical': direction === 'vertical'
  });
  return /*#__PURE__*/React.createElement("div", {
    className: componentClassName,
    ref: fwdRef
  }, /*#__PURE__*/React.createElement("div", {
    className: containerClassName,
    ref: ref
  }, children), /*#__PURE__*/React.createElement(ScrollMarker, {
    position: "left",
    visible: showLeft
  }), /*#__PURE__*/React.createElement(ScrollMarker, {
    position: "right",
    visible: showRight
  }), /*#__PURE__*/React.createElement(ScrollMarker, {
    position: "top",
    visible: showTop
  }), /*#__PURE__*/React.createElement(ScrollMarker, {
    position: "bottom",
    visible: showBottom
  }));
};
const Fwd = /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(ScrollIndicator, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'ScrollIndicator';
export default Fwd;